import { Injectable } from '@angular/core';
import {
  AddSqaContent,
  LoginFormValidate,
  RequestAddSqa,
  ResponseAddSqa
} from '@com-delta-idp/mnx-login-ui';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CoreUtilService } from '../../../utils/core-util.service';
import { LoginHelperUtils } from '../../../utils/login-helper.utils';
import { AddSqaAdaptersService } from '../../adapters/add-sqa/add-sqa.adapters.service';
import { STANDALONE_PAGE_URL, VIEW_NAME } from '../../constants/login-constant';
import { LoginModalContent } from '../../models/aem';
import { LoginContentService } from '../../services';
import { AddSqaApiService } from '../../services/add-sqa/add-sqa-api.service';
import { AddSqaHelperService } from '../../services/add-sqa/add-sqa-helper.service';
import { AddSqaFacadeHelperService } from './add-sqa-facade-helper.service';

@Injectable({
  providedIn: 'root'
})
export class AddSqaFacadeService extends AddSqaFacadeHelperService {
  aemContent!: LoginModalContent;
  addSqaContent$: BehaviorSubject<AddSqaContent | null> =
    new BehaviorSubject<AddSqaContent | null>(null);

  unsubscribe$ = new Subject();

  constructor(
    private contentService: LoginContentService,
    private addSqaAdapterService: AddSqaAdaptersService,
    private addSqaApiService: AddSqaApiService,
    private addSqaHelperService: AddSqaHelperService,
    protected loginHelperUtils: LoginHelperUtils,
    protected coreUtilService: CoreUtilService
  ) {
    super(loginHelperUtils);
  }

  getAddSqaPageContent(): void {
    this.contentService.getAddSqaContent().subscribe(async (response) => {
      const [aemResponse, sqaList] = response;
      this.aemContent = aemResponse;
      const content = this.addSqaAdapterService.getAddSqaContent(
        aemResponse,
        sqaList
      );
      this.addSqaContent$.next(content);
    });
  }

  addSqaFormFocusChange(fieldName: string): void {
    const content: AddSqaContent = this.getAddSqaContent();
    LoginFormValidate.touchControlAddSqa(fieldName, content);
  }

  formValueChange(): void {
    const content: AddSqaContent = this.getAddSqaContent();
    if (
      content.formAttributes.submitAttempt &&
      content.formAttributes.errorAlertBanner &&
      content.formAttributes.errorCount
    ) {
      this.getAddSqaContent().formGroup.valueChanges.subscribe(() => {
        this.getErrorCount();
      });
    }
  }

  onAddSqaFormSubmit(): void {
    if (this.validateForm()) {
      return;
    }
    this.setActionButtonDisable(true);
    const loginBffRequestPayload: RequestAddSqa =
      this.addSqaAdapterService.setAddSqaRequestObj(this.getAddSqaContent());
    this.addSqaApiService
      .loginBff(loginBffRequestPayload)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => this.setActionButtonDisable(false))
      )
      .subscribe(
        (response) => {
          if (response.errors !== undefined && response.errors.length > 0) {
            this.handleError(response.errors, this.aemContent);
          } else if (this.isUserLoggedIn(response)) {
            this.resetForm();
            this.coreUtilService.setCookieName(
              'accountType',
              'true-false',
              0,
              true
            );
            this.handleResponseAddSqa(response);
          }
        },
        (error) => this.handleError(error, this.aemContent)
      );
  }

  isUserLoggedIn(response: ResponseAddSqa): boolean {
    return (
      response.isLoggedIn &&
      response.formNameSubmitted ===
        STANDALONE_PAGE_URL[VIEW_NAME.LOGIN_CREDENTIALS_SCREEN]
    );
  }

  setActionButtonDisable(value: boolean): void {
    this.getAddSqaContent().formAttributes.disableActionButton = value;
  }

  validateForm(): boolean {
    LoginFormValidate.addSqaFormControlsOnSubmit(this.getAddSqaContent());
    this.getAddSqaContent().formAttributes.submitAttempt = true;
    return this.getErrorCount();
  }

  getErrorCount(): boolean {
    const errorCount: number = LoginFormValidate.validateFormFields(
      this.getAddSqaContent().formGroup
    );
    this.getAddSqaContent().formAttributes.errorCount = errorCount;
    this.getAddSqaContent().formAttributes.errorAlertBanner =
      LoginFormValidate.getErrorAlert(this.getAddSqaContent().formGroup);
    return errorCount > 0;
  }

  resetForm(): void {
    this.getAddSqaContent().formAttributes =
      this.addSqaHelperService.getFormAttributes();
    this.getAddSqaContent().hasError = false;
    this.displayOverylayMessage(false);
    this.getAddSqaContent().formGroup?.reset();
  }
}
